import {
  AfterViewInit,
  Component,
  Inject,
  Injectable,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { I18nService } from '@app/i18n';
import { environment } from '@env/environment';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { filter, map, merge, switchMap } from 'rxjs';
import { untilDestroyed } from '@core/until-destroyed';
import { MatSidenav } from '@angular/material/sidenav';
import { SideNavService } from '@shared/services/web/side-nav.service';
import { AuthenticationService } from '@app/auth/authentication.service';
import { CredentialsService } from '@shared/services/web/credentials.service';
import { FilterPropertiesDataService } from '@shared/services/web/filter-properties.data.service';
import { MatDialog } from '@angular/material/dialog';
import { ASSETS_API_URL } from '@app/app.constants';
import { IconsService } from '@shared/services/web/icons.service';
import { SignInModalComponent } from './security/modals/sign-in-modal/sign-in-modal.component';
import { LoginModalComponent } from './security/modals/login-modal/login-modal.component';
import { InvestCarouselResponse } from './@shared/model/invest.model';
import { ActionsService } from './@shared/services/web/actions.service';
import { VersionCheckService } from './@core/cache-clear/service/version-check.service';
import { WeCallYouComponent } from './@shared/components/we-call-you/we-call-you.component';
import { TrackingService } from './@shared/services/web/tracking.service';
import { CookieFirstService } from './@shared/services/web/cookie-first.service';
import { ScrollService } from './@core/scroll.service';

export interface Titles {
  [lang: string]: {
    [title: string]: string;
  };
}

export interface Descriptions {
  [lang: string]: {
    [desc: string]: string;
  };
}
@Injectable({
  providedIn: 'root',
})
export class SEOService {
  descriptions: Descriptions = {
    es: {
      'Invierte en un piso alquilado con información verificada y análisis financieros. Inviertis, rentabilidad inmediata.':
        'Invierte en un piso alquilado con información verificada y análisis financieros. Inviertis, rentabilidad inmediata.',
      'Vende online tu piso alquilado en nuestra plataforma, sin costes de fricción, ni mejoras en el inmueble. Más de 8.000 inversores esperan pisos como el tuyo.':
        'Vende online tu piso alquilado en nuestra plataforma, sin costes de fricción, ni mejoras en el inmueble. Más de 8.000 inversores esperan pisos como el tuyo.',
    },
    en: {
      'Invierte en un piso alquilado con información verificada y análisis financieros. Inviertis, rentabilidad inmediata.':
        'Invest in a rented apartment with verified information and financial analysis. You invest, immediate profitability.',
      'Vende online tu piso alquilado en nuestra plataforma, sin costes de fricción, ni mejoras en el inmueble. Más de 8.000 inversores esperan pisos como el tuyo.':
        'Sell ​​your rented apartment online on our platform, without friction costs. More than 8,000 investors are waiting for apartments with a tenant like yours.',
    },
  };

  titles: Titles = {
    es: {
      'Inversión rentable: Invierte en un piso alquilado':
        'Inviertis: Inversión rentable. Invierte en un piso alquilado',
      'Venta de pisos alquilados totalmente online, sin visitas y sin reacondicionar la vivienda para venderla':
        'Venta de pisos alquilados totalmente online, sin visitas y sin reacondicionar la vivienda para venderla',
    },
    en: {
      'Inversión rentable: Invierte en un piso alquilado':
        'Inviertis: Profitable investment. Invest in a rented apartment',
      'Venta de pisos alquilados totalmente online, sin visitas y sin reacondicionar la vivienda para venderla':
        'Sale of rented apartments completely online, without visits and without reconditioning the house to sell it',
    },
  };
  constructor(
    private meta: Meta,
    @Inject(DOCUMENT) private dom: any,
    private translateService: TranslateService
  ) {}

  updateTitle(title: string) {
    const newTitle = this.titles[this.translateService.currentLang][title]
      ? this.titles[this.translateService.currentLang][title]
      : title;
    this.meta.updateTag({
      property: 'og:title',
      content: newTitle,
    });
    this.meta.updateTag({
      name: 'title',
      content: newTitle,
    });
  }

  updateOgUrl(url: string) {
    const full_url = 'https://inviertispro.com' + url;
    this.meta.updateTag({ property: 'og:url', content: full_url });
  }

  updateDescription(desc: string) {
    this.meta.updateTag({
      name: 'description',
      content:
        this.descriptions[this.translateService.currentLang][desc] ?? desc,
    });
  }

  updateCanonicalUrl(url: string) {
    if (url.includes('/en/')) {
      url = url.replace('/en/', '/es/');
    }
    if (url.includes('/es/')) {
      url = url.replace('/es/', '/es/');
    }
    const full_url = 'https://inviertispro.com' + url;
    const head = this.dom.getElementsByTagName('head')[0];
    let element: HTMLLinkElement =
      this.dom.querySelector(`link[rel='canonical']`) || null;
    if (element == null) {
      element = this.dom.createElement('link') as HTMLLinkElement;
      head.appendChild(element);
    }
    element.setAttribute('rel', 'canonical');
    element.setAttribute('href', full_url);
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  public picturesServer = ASSETS_API_URL;
  title = 'inviertis-web';
  showHead = true;
  breadcrumbs: { label: string; path: string | null; label_en?: string }[] = [];
  language = '';
  public flagAreaPrivada = false;
  @ViewChild('sidenav') public sidenav!: MatSidenav;
  @ViewChild('sidenavlogin') public sidenavlogin!: MatSidenav;

  showSliderInvest = false;
  showSliderSell = false;
  showSliderHowItWorks = false;

  // Propiedades para la notificación de versión
  showVersionUpdate = false;
  newVersion = '';
  currentVersion = '';

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    public translateService: TranslateService,
    private iconService: IconsService,
    private spinner: NgxSpinnerService,
    private i18nService: I18nService,
    public _seoService: SEOService,
    private renderer2: Renderer2,
    private sideNavService: SideNavService,
    public filterPropertiesDataService: FilterPropertiesDataService,
    private authenticationService: AuthenticationService,
    public credentialsService: CredentialsService,
    @Inject(DOCUMENT) private _document: any,
    @Inject(PLATFORM_ID) private platformId: any,
    private dialog: MatDialog,
    private actionsService: ActionsService,
    private versionCheckService: VersionCheckService,
    private trackingService: TrackingService,
    private cookieFirstService: CookieFirstService,
    public scrollService: ScrollService
  ) {}

  ngOnInit(): void {
    this.i18nService.init(
      environment.defaultLanguage,
      environment.supportedLanguages
    );

    // Inicializar verificación de versiones
    this.versionCheckService.startVersionCheck();

    // Inicializar tracking scripts (solo en producción o HTTPS)
    this.trackingService.initializeTracking();

    if (
      this.router.url === '/es/invertir/banner' ||
      this.router.url === '/en/invertir/banner' ||
      this.router.url.includes('/inversores')
    ) {
      this.showHead = false;
    }
    this.language = this.translateService.currentLang;
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        if (event instanceof NavigationStart) {
          this.spinner.show('global');
        } else if (event instanceof NavigationEnd) {
          this.spinner.hide('global');
        } else if (
          event instanceof NavigationError &&
          event.error.status === 404
        ) {
          this.spinner.hide('global');
        } else if (event instanceof NavigationCancel) {
          this.spinner.hide('global');
        }

        event.url.includes('tablero-de-usuario')
          ? (this.flagAreaPrivada = true)
          : (this.flagAreaPrivada = false);

        event.url.includes('/inversores')
          ? (this.showHead = false)
          : (this.showHead = true);
      });


    const onNavigationEnd = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd)
    );

    // Change page title on navigation or language change, based on route data
    merge(this.translateService.onLangChange, onNavigationEnd)
      .pipe(
        map(() => {
          let route = this.activatedRoute;
          if (isPlatformBrowser(this.platformId)) {
            while (route.firstChild) {
              route = route.firstChild;
            }
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        switchMap((route) => route.data),
        untilDestroyed(this)
      )
      .subscribe((event: any) => {
        const title = event.title;
        const description = event.description;
        const url = this.router.url;
        if (title) {
          this.titleService.setTitle(this.translateService.instant(title));
          this._seoService.updateTitle(title);
        }
        if (description) {
          this._seoService.updateDescription(description);
        }
        this._seoService.updateOgUrl(url);
        this._seoService.updateCanonicalUrl(url);

        this.generateLinks(url, 'es-ES', 'alternate');
        this.generateLinks(url, 'en-GB', 'alternate');
        this.generateLinks(url, 'x-default', 'alternate');
      });
  }
  openCallModal() {
    this.dialog.open(WeCallYouComponent, {
      maxWidth: '95vw',
      panelClass: 'noPadding',
    });
  }
  ngAfterViewInit(): void {
    this.sideNavService.setSidenav(this.sidenav);

    // Load CookieFirst after all components are initialized
    // Use setTimeout to ensure this runs after the current change detection cycle
    setTimeout(() => {
      this.cookieFirstService
        .loadCookieFirst()
        .then((response) => response)
        .catch((error) => {
          console.error('Error loading CookieFirst:', error);
        });
    }, 100);
  }

  goNavigateToUrl(path: string): string | void {
    if (path === 'invertir') {
      this.showSliderInvest = true;
      this.showSliderSell = false;
      this.showSliderHowItWorks = false;
      if (
        !this.router.url.includes('invertir/propiedad') &&
        (this.router.url.includes('invertir') ||
          this.router.url.includes('invertir?'))
      ) {
        return '';
      } else {
        sessionStorage.removeItem('search');
        const customCreateUrl = `/${this.translateService.currentLang}/${path}`;
        this.router.navigate([customCreateUrl]).then(() => {
          this.scrollService.scrollToTopImmediate();
        });
      }
    } else if (path === 'vende') {
      this.showSliderInvest = false;
      this.showSliderSell = true;
      this.showSliderHowItWorks = false;
    } else if (path === 'como-funciona') {
      this.showSliderInvest = false;
      this.showSliderSell = false;
      this.showSliderHowItWorks = true;
    } else {
      const customCreateUrl = `/${this.translateService.currentLang}/${path}`;
      this.sidenav.toggle();
      this.router.navigate([customCreateUrl]).then(() => {
        this.scrollService.scrollToTopImmediate();
      });
    }
  }

  async logout() {
    (await this.authenticationService.logout()).subscribe(() => {
      this.router.navigate(['/'], { replaceUrl: true }).then(() => {
        this.scrollService.scrollToTopImmediate();
      });
    });
    this.closeSideNavs();
  }

  goToBlog(): void {
    this._document.location.href = 'https://blog.inviertispro.com';
  }

  navigateToUrl(path: string): void {
    const customCreateUrl = `/${this.translateService.currentLang}/${path}`;
    this.router.navigate([customCreateUrl]).then(() => {
      this.scrollService.scrollToTopImmediate();
    });
    if (path === 'invertir') {
      this.filterPropertiesDataService.putFiltersProperties(null);
    }
    this.sidenav.close();
  }
  get username(): string | undefined | null {
    const credentials = this.credentialsService.credentials;
    return credentials ? credentials.first_name : null;
  }

  goToRoute(migajas: any) {
    // se pone comprobacion para los breadcrumbs que van al listado filtrado, ya que tienen un comportamiento diferente
    if (migajas.type !== 'dynamic') {
      if (migajas.label == 'Home') {
        this.router.navigate([migajas.path], { replaceUrl: true });
      } else {
        this.router.navigate(
          [this.translateService.currentLang + migajas.path],
          { replaceUrl: true }
        );
      }
    } else {
      this.router
        .navigate([this.translateService.currentLang + migajas.path], {
          queryParams: { pagina: 1 },
          replaceUrl: true,
        })
        .then(() => {
          window.location.reload();
        });
    }
  }

  ngOnDestroy() {
    this.i18nService.destroy();
  }

  generateLinks(url: string, lang: string, rel?: string) {
    if (url.includes('page-not-found')) {
      return;
    }

    switch (lang) {
      case 'en-GB':
        url = url.replace('/es/', '/en/');
        break;
      case 'es-ES':
        url = url.replace('/en/', '/es/');
        break;
      case 'x-default':
        url = url.replace('/en/', '/es/');
        url = url.replace('/es/', '/es/');
        break;
      default:
        break;
    }

    // Evita acumular <link> duplicados en cada cambio de ruta/idioma: se reemplazan
    // TODOS los que ya existan para este hreflang (puede haber mas de uno si
    // NavigationEnd y onLangChange dispararon el mismo evento casi a la vez).
    const existingLinks = this._document.head.querySelectorAll(
      `link[hreflang="${lang}"]`
    );
    existingLinks.forEach((existingLink: Element) =>
      this.renderer2.removeChild(this._document.head, existingLink)
    );

    const link = this.renderer2.createElement('link');
    if (rel) {
      link.rel = rel;
    }
    link.hreflang = lang;
    link.href = environment.serverFrontUrl + url;
    this.renderer2.appendChild(this._document.head, link);
  }

  generateLang(url: string, lang: string) {
    const langUniversal = this._document.querySelector('html');
    this.renderer2.setAttribute(langUniversal, 'lang', lang);
  }
  closeSideNavs() {
    this.sidenavlogin.close();
    this.sidenav.close();
  }

  openSignInModal() {
    this.dialog.open(SignInModalComponent, {
      maxWidth: '90vw',
      panelClass: 'icon-logo-modal',
      id: 'sign-in-modal-header',
    });
  }

  openLoginModal() {
    this.dialog.open(LoginModalComponent, {
      maxWidth: '90vw',
      panelClass: 'icon-logo-modal',
    });
  }

  onCardSelected(card: InvestCarouselResponse) {
    this.actionsService.investCarousel$.next(card);
  }

  /**
   * Maneja la actualización de versión cuando el usuario hace clic en "Actualizar"
   */
  handleVersionUpdate(): void {
    this.showVersionUpdate = false;
    // Recargar la página para aplicar la nueva versión
    window.location.reload();
  }

  /**
   * Descarta la notificación de actualización
   */
  dismissVersionUpdate(): void {
    this.showVersionUpdate = false;
  }

  /**
   * Muestra la notificación de nueva versión disponible
   */
  showVersionUpdateNotification(newVersion: string): void {
    this.newVersion = newVersion;
    this.currentVersion = this.versionCheckService.getCurrentVersion();
    this.showVersionUpdate = true;
  }
}
